import * as React from 'react'
import { mergeClasses, Tooltip } from '@fluentui/react-components'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { formatTimeInMinutes } from '../../../../helpers/numberHelper'
import CalendarCell, { colorClassName } from './CalendarCell'
import CalendarTurnover from './CalendarTurnover'
import { useTimeCalendarStyles } from './timeCalendarStyles'
import { CalendarWeek, DailyTotalDto, DayColor, resolveDayColor, resolveWeekColor, sumDurations, sumTurnoverPerCurrency } from './timeCalendarHelper'

type Props = {
    week: CalendarWeek
    totals: Map<string, DailyTotalDto>
    normInMinutes?: number
    colorsActive: boolean
    dayThresholdMinutes: number
    hideWeekend: boolean
    compact: boolean
    onSelectDate: (date: string) => void
}

/**
 * One week: the week number, a cell per day and the week total.
 */
export default function CalendarRow(props: Props) {
    const styles = useTimeCalendarStyles()
    const { t: translate, i18n } = useTranslation()

    const dayCount = props.hideWeekend ? 5 : 7
    const days = Array.from({ length: dayCount }, (_value, index) => props.week.monday.plus({ days: index }))
    const weekDays = Array.from({ length: 7 }, (_value, index) => props.totals.get(props.week.monday.plus({ days: index }).toISODate()))
    const weekMinutes = sumDurations(weekDays.filter(Boolean))
    const isCurrentWeek = props.week.monday.hasSame(DateTime.local(), 'week')
    const weekColor = resolveWeekColor(props.week.monday, weekMinutes, props.normInMinutes, props.colorsActive)

    return (
        <tr>
            <td
                className={mergeClasses(
                    styles.cell,
                    props.compact ? styles.weekCellCompact : styles.weekCell,
                    props.compact && styles.cellCompact,
                    isCurrentWeek && styles.currentWeekCell,
                )}
            >
                {/* The tooltip renders its portal next to its child, which a <tr> may not contain: keep it inside the cell. */}
                <Tooltip
                    withArrow
                    relationship={'label'}
                    content={
                        <div className={styles.tooltipContent}>
                            <strong>{translate('time.calendar.tooltip.week', { week: props.week.weekNumber })}</strong>
                            <span>
                                {props.week.monday.setLocale(i18n.language).toFormat('d LLL')} -{' '}
                                {props.week.monday.plus({ days: 6 }).setLocale(i18n.language).toFormat('d LLL yyyy')}
                            </span>
                        </div>
                    }
                >
                    <span>{props.week.weekNumber}</span>
                </Tooltip>
            </td>

            {days.map(date => (
                <CalendarCell
                    key={date.toISODate()}
                    date={date}
                    total={props.totals.get(date.toISODate())}
                    color={resolveDayColor(date, props.totals.get(date.toISODate())?.duration ?? 0, props.dayThresholdMinutes, props.colorsActive)}
                    compact={props.compact}
                    onSelectDate={props.onSelectDate}
                />
            ))}

            <td
                className={mergeClasses(
                    styles.cell,
                    styles.totalCell,
                    props.compact && styles.totalCellCompact,
                    props.compact && styles.cellCompact,
                    weekColor !== DayColor.None && colorClassName(weekColor, styles),
                )}
            >
                <Tooltip
                    withArrow
                    relationship={'label'}
                    content={
                        <div className={styles.tooltipContent}>
                            <strong>{translate('time.calendar.tooltip.week', { week: props.week.weekNumber })}</strong>
                            <span>
                                {weekMinutes
                                    ? translate('time.calendar.tooltip.duration', { value: formatTimeInMinutes(weekMinutes) })
                                    : translate('time.calendar.duration.none')}
                            </span>
                            <CalendarTurnover
                                amounts={sumTurnoverPerCurrency(weekDays.filter(Boolean))}
                                labelKey={'time.calendar.tooltip.turnover'}
                            />
                        </div>
                    }
                >
                    <span>{formatTimeInMinutes(weekMinutes)}</span>
                </Tooltip>
            </td>
        </tr>
    )
}
